import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import { ReportCardActionsProvider } from '../../ReportCardAccess/report-card-actions.context';
import { PlayerRankingReport } from './player-ranking-report.component';
import { toWeighted } from '../../../lib/helpers/conversion.helpers';
import { SportTerminology } from '../../../lib/types';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { IConcludePlayerOptions } from '../types/conclude-player-options.types';

interface IPlayerRankingReportContainer {
  playerAssessments: IPlayerAssessment[];
  ageGroup?: IAgeGroup;
  fetchError: boolean;
  loading: boolean;
  assignLevel: Function;
  assignTeam: Function;
  concludePlayers: Function;
  concludeEmailTemplateOptions: IConcludePlayerOptions[];
  goalieFilter: 'goalie' | 'other';
}

export const PlayerRankingReportContainer: React.FC<IPlayerRankingReportContainer> = ({
  playerAssessments,
  ageGroup,
  loading,
  fetchError,
  assignLevel,
  assignTeam,
  concludePlayers,
  concludeEmailTemplateOptions,
  goalieFilter
}) => {
  const termLookup = useCompanySportTerminology();

  const [selected, setSelected] = useState<IPlayerAssessment[]>([]);
  const [level, setLevel] = useState<string>('');
  const [team, setTeam] = useState<string>('');
  const [sendNotifications, setSendNotifications] = useState<boolean>(false);
  const [rankedPlayerAssessments, setRankedPlayerAssessments] = useState<IPlayerAssessment[]>([]);

  const [ concludeEmailTemplate, setConcludeEmailTemplate ] = useState<string>(concludeEmailTemplateOptions[0].value);


  useEffect(() => {
    setRankedPlayerAssessments(_.orderBy(playerAssessments, playerAssessment => Number(playerAssessment.adjustedScore === "-" ? 0 : playerAssessment.adjustedScore), ['desc']));
  }, [playerAssessments]);

  const handleExportReports = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    exportReports({ ageGroupName: ageGroup?.name || '', athleteTerm: termLookup[SportTerminology.ATHLETE], playerAssessments: rankedPlayerAssessments });
  }

  const handleAssignLevel = () => {
    assignLevel(_.map(selected, '_id'), level);
    setLevel('');
    setSelected([]);
  };

  const handleAssignTeam = () => {
    assignTeam(_.map(selected, '_id'), team);
    setTeam('');
    setSelected([]);
  };

  const handleConcludePlayers = () => {
    concludePlayers(_.map(selected, '_id'), sendNotifications, concludeEmailTemplate);
    setSendNotifications(false);
    setSelected([]);
  }

  // One set of report card writes and one confirm dialog for the whole table, not one per row.
  return (
    <ReportCardActionsProvider>
      <PlayerRankingReport
        ageGroup={ageGroup}
        level={level}
        setLevel={setLevel}
        assignLevel={handleAssignLevel}
        team={team}
        setTeam={setTeam}
        sendNotifications={sendNotifications}
        setSendNotifications={setSendNotifications}
        assignTeam={handleAssignTeam}
        selected={selected}
        setSelected={setSelected}
        exportReports={handleExportReports}
        playerAssessments={rankedPlayerAssessments}
        fetchError={fetchError}
        loading={loading}
        concludePlayers={handleConcludePlayers}
        concludeEmailTemplate={concludeEmailTemplate}
        setConcludeEmailTemplate={setConcludeEmailTemplate}
        concludeEmailTemplateOptions={concludeEmailTemplateOptions}
        goalieFilter={goalieFilter}
      />
    </ReportCardActionsProvider>
  );
};

interface IExportReports {
  ageGroupName: string;
  athleteTerm: string;
  playerAssessments: IPlayerAssessment[];
}

function exportReports({ ageGroupName, athleteTerm, playerAssessments }: IExportReports) {
  let csvContent: string = 'data:text/csv;charset=utf-8,';
  let encodedURI;
  csvContent += `Rank,Level,Team,UNBIAS™ Result,${athleteTerm} Last Name,${athleteTerm} First Name,Nickname,Position,Contact Name,Contact Email,Contact Phone\n`;

  _.each(playerAssessments, (playerAssessment, index) => {
    let row = '';

    row = `${index + 1},${playerAssessment.level || ''},${playerAssessment.team || ''},${toWeighted(playerAssessment.adjustedScore)},${playerAssessment.player.lastName},${playerAssessment.player.firstName},${playerAssessment.player.nickname},${playerAssessment.position.name}`;
    _.each(playerAssessment.contacts, (contact) => {
      row += `,${contact.name},${contact.email},${contact.phone}`;
    });

    csvContent += index < playerAssessments.length ? row + '\n' : row;
  });

  encodedURI = encodeURI(csvContent);

  let exportLink = document.createElement("a");
  exportLink.setAttribute("href", encodedURI);
  exportLink.setAttribute("download", `player-rankings-${ageGroupName}.csv`);
  exportLink.setAttribute("target", "_blank");
  document.body.appendChild(exportLink); // Required for FF

  exportLink.click();

  return false;
}
